/**
 * In-app website alerts: booking accepted, Join-ready, and new order/sale messages.
 * Read state lives in localStorage so opening a thread clears the badge without spam.
 */

import {
  JOIN_SESSION_ENTER_PARAM,
  JOIN_SESSION_HASH,
  isJoinableDashboardNotification,
  isStillJoinableTransaction,
  isTerminalJoinTransition,
  liveActiveSessionNotifications,
  notificationBookedEndMs,
} from './sessionNotifications';
import { resolveSessionFormat, sessionFormatDisplayName } from './sessionPricing';
import { NAV_MODE_TALKER } from './siteNavMode';

export const IN_APP_NOTIFICATION_POLL_MS = 60000;
export const IN_APP_NOTIFICATION_STORAGE_KEY = 'sl-inapp-alerts-v1';
export const IN_APP_NOTIFICATION_CHANGE_EVENT = 'sl-inapp-alerts-change';
export const IN_APP_NOTIFICATION_REFRESH_EVENT = 'sl-inapp-alerts-refresh';
export const IN_APP_NOTIFICATION_MAX_ITEMS = 8;

/**
 * Talker mode is customer/orders. Listener mode is provider/sales.
 *
 * @param {'talker'|'listener'} mode
 * @returns {'customer'|'provider'}
 */
export const inAppAlertRoleForNavMode = mode =>
  mode === NAV_MODE_TALKER ? 'customer' : 'provider';

/**
 * Keep only alerts for one marketplace role.
 *
 * @param {Array<Object>} [items]
 * @param {'customer'|'provider'} role
 * @returns {Array<Object>}
 */
export const filterInAppAlertsByRole = (items, role) =>
  (items || []).filter(item => {
    const itemRole =
      item?.role === 'provider' || item?.role === 'listener' || item?.role === 'sale'
        ? 'provider'
        : 'customer';
    return itemRole === role;
  });

/**
 * Newest alerts for one marketplace role, capped for the bell list.
 *
 * @param {Array<Object>} [items]
 * @param {'customer'|'provider'} role
 * @param {number} [limit]
 * @returns {Array<Object>}
 */
export const takeInAppAlertsForRole = (items, role, limit = IN_APP_NOTIFICATION_MAX_ITEMS) =>
  filterInAppAlertsByRole(items, role).slice(0, limit);

/**
 * Alerts for the current Talker/Listener menu. Dual-role /notifications
 * defaults to Listener; if that side is empty, show the other side so a
 * Talker still sees "Maya accepted your booking".
 *
 * @param {Array<Object>} [items]
 * @param {'talker'|'listener'} mode
 * @param {number} [limit]
 * @returns {Array<Object>}
 */
export const takeInAppAlertsForNavMode = (
  items,
  mode,
  limit = IN_APP_NOTIFICATION_MAX_ITEMS,
  { allowOtherSide = true } = {}
) => {
  const role = inAppAlertRoleForNavMode(mode);
  const preferred = takeInAppAlertsForRole(items, role, limit);
  if (preferred.length > 0 || !allowOtherSide) {
    return preferred;
  }
  const other = role === 'customer' ? 'provider' : 'customer';
  return takeInAppAlertsForRole(items, other, limit);
};

/**
 * Keep enough of each role so Talker and Listener bells do not starve each other.
 *
 * @param {Array<Object>} [items]
 * @param {number} [limit]
 * @returns {Array<Object>}
 */
export const takeInAppAlertsPerRole = (items, limit = IN_APP_NOTIFICATION_MAX_ITEMS) => {
  const customer = takeInAppAlertsForRole(items, 'customer', limit);
  const provider = takeInAppAlertsForRole(items, 'provider', limit);
  return [...customer, ...provider].sort(
    (a, b) => (Date.parse(b.createdAt) || 0) - (Date.parse(a.createdAt) || 0)
  );
};

export const IN_APP_KIND_ACCEPTED = 'accepted';
export const IN_APP_KIND_MESSAGE = 'message';
export const IN_APP_KIND_JOIN_READY = 'join_ready';
export const IN_APP_KIND_PENDING_REQUEST = 'pending_request';
export const IN_APP_KIND_ACCEPT_REMINDER = 'accept_reminder';
export const IN_APP_KIND_DECLINED = 'declined';
export const IN_APP_KIND_CANCELLED = 'cancelled';
export const IN_APP_KIND_REVIEW = 'review';
export const IN_APP_KIND_PAYOUT = 'payout';
export const IN_APP_KIND_TIP = 'tip';
export const IN_APP_KIND_REMOVED = 'removed';

const JOIN_KINDS = new Set([IN_APP_KIND_JOIN_READY, 'starting_soon', 'start_now', 'buzz']);
export const IN_APP_ALERT_KINDS = new Set([
  IN_APP_KIND_ACCEPTED,
  IN_APP_KIND_MESSAGE,
  IN_APP_KIND_PENDING_REQUEST,
  IN_APP_KIND_ACCEPT_REMINDER,
  IN_APP_KIND_DECLINED,
  IN_APP_KIND_CANCELLED,
  IN_APP_KIND_REVIEW,
  IN_APP_KIND_PAYOUT,
  IN_APP_KIND_TIP,
  IN_APP_KIND_REMOVED,
  ...JOIN_KINDS,
]);
const KIND_PRIORITY = {
  buzz: 4,
  [IN_APP_KIND_JOIN_READY]: 3,
  starting_soon: 3,
  start_now: 3,
  [IN_APP_KIND_MESSAGE]: 2,
  [IN_APP_KIND_ACCEPTED]: 1,
  [IN_APP_KIND_PENDING_REQUEST]: 1,
  [IN_APP_KIND_ACCEPT_REMINDER]: 1,
  [IN_APP_KIND_DECLINED]: 1,
  [IN_APP_KIND_CANCELLED]: 1,
  [IN_APP_KIND_REVIEW]: 1,
  [IN_APP_KIND_PAYOUT]: 1,
  [IN_APP_KIND_TIP]: 2,
};

const PUBLIC_KEYS = [
  'id',
  'kind',
  'createdAt',
  'transactionId',
  'role',
  'otherDisplayName',
  'listingTitle',
  'preview',
  'sessionFormat',
  'startsAt',
  'endsAt',
  'lastTransition',
  'sessionEnded',
  'outcome',
  'joinName',
  'joinParams',
  'listingId',
  'rating',
  'startNow',
  'clockStarted',
  'bothJoined',
  'suggestions',
  'tipAmount',
  'tipEarn',
  'sweetTreat',
];

const FORBIDDEN_KEY_RE = /^(phone|email|address|location|geolocation|neighborhood|origin|ipAddress|ip)$/i;
const PHONE_RE = /(?:\+\d{1,3}[\s.-]?)?(?:\(?\d{3}\)?[\s.-]?)\d{3}[\s.-]?\d{4}/;
const EMAIL_RE = /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/i;

const emptyUserState = () => ({
  readAtByTx: {},
  toastedIds: [],
  deletedAtByTx: {},
});

/**
 * Strip contact details from a display name.
 *
 * @param {unknown} name
 * @returns {string}
 */
export const safeAlertName = name => {
  const raw = typeof name === 'string' ? name.trim() : '';
  if (!raw || PHONE_RE.test(raw) || EMAIL_RE.test(raw)) {
    return 'Someone';
  }
  return raw.slice(0, 80);
};

/**
 * Strip contact details from a listing title.
 *
 * @param {unknown} title
 * @returns {string}
 */
export const safeAlertTitle = title => {
  const raw = typeof title === 'string' ? title.trim() : '';
  if (!raw || PHONE_RE.test(raw) || EMAIL_RE.test(raw)) {
    return 'Talk session';
  }
  return raw.slice(0, 120);
};

/**
 * Short message preview for the bell list. Never keeps a phone or email.
 *
 * @param {unknown} text
 * @returns {string|null}
 */
export const safeMessagePreview = text => {
  const raw = typeof text === 'string' ? text.replace(/\s+/g, ' ').trim() : '';
  if (!raw) {
    return null;
  }
  if (PHONE_RE.test(raw) || EMAIL_RE.test(raw)) {
    return 'New message';
  }
  return raw.slice(0, 80);
};

/**
 * Keep only public alert fields.
 *
 * @param {Object} raw
 * @returns {Object|null}
 */
export const sanitizeInAppAlert = raw => {
  if (!raw || typeof raw !== 'object') {
    return null;
  }
  const item = {};
  PUBLIC_KEYS.forEach(key => {
    if (raw[key] !== undefined && !FORBIDDEN_KEY_RE.test(key)) {
      item[key] = raw[key];
    }
  });
  if (item.otherDisplayName) {
    item.otherDisplayName = safeAlertName(item.otherDisplayName);
  }
  if (item.listingTitle) {
    item.listingTitle = safeAlertTitle(item.listingTitle);
  }
  if (item.preview) {
    item.preview = safeMessagePreview(item.preview);
  }
  if (item.suggestions !== undefined) {
    const clean = (Array.isArray(item.suggestions) ? item.suggestions : [])
      .filter(s => s && typeof s.start === 'string' && typeof s.end === 'string')
      .slice(0, 3)
      .map(s => ({ start: s.start, end: s.end }));
    if (clean.length) {
      item.suggestions = clean;
    } else {
      delete item.suggestions;
    }
  }
  const inferredFormat = resolveSessionFormat({
    sessionFormat: item.sessionFormat || raw.sessionFormat,
    priceVariantName: raw.priceVariantName,
    protectedData: raw.protectedData,
  });
  if (inferredFormat === 'voice' || inferredFormat === 'video' || inferredFormat === 'chat') {
    item.sessionFormat = inferredFormat;
  } else {
    delete item.sessionFormat;
  }
  if (item.startNow !== true) {
    delete item.startNow;
  }
  if (!item.id || !item.kind || !item.transactionId) {
    return null;
  }
  if (!IN_APP_ALERT_KINDS.has(item.kind)) {
    return null;
  }
  if (item.role !== 'customer' && item.role !== 'provider') {
    item.role = item.role === 'sale' ? 'provider' : 'customer';
  }
  if (typeof item.listingId !== 'string' || !item.listingId) {
    delete item.listingId;
  }
  const rating = Number(item.rating);
  if (Number.isFinite(rating) && rating >= 1 && rating <= 5) {
    item.rating = Math.round(rating);
  } else {
    delete item.rating;
  }
  return attachAlertJoinRoute(item);
};

/**
 * Order/sale Join target for a live session alert. Params are id-only.
 *
 * @param {Object} item
 * @returns {Object}
 */
export const attachAlertJoinRoute = item => {
  if (!item || !JOIN_KINDS.has(item.kind)) {
    if (item?.joinParams && typeof item.joinParams === 'object') {
      const id = typeof item.joinParams.id === 'string' ? item.joinParams.id : undefined;
      item.joinParams = id ? { id } : undefined;
    }
    return item;
  }
  const id =
    (typeof item.joinParams?.id === 'string' && item.joinParams.id) ||
    (typeof item.transactionId === 'string' && item.transactionId) ||
    '';
  if (!id) {
    return item;
  }
  const joinName =
    item.joinName === 'OrderDetailsPage' || item.joinName === 'SaleDetailsPage'
      ? item.joinName
      : item.role === 'provider'
      ? 'SaleDetailsPage'
      : 'OrderDetailsPage';
  item.joinName = joinName;
  item.joinParams = { id };
  return item;
};

/**
 * Live Join rows for the sticky Continue chat banner. Not collapsed by a
 * newer message — that list is one row per booking for the bell only.
 *
 * @param {Array} [items]
 * @param {number} [nowMs]
 * @returns {Array}
 */
export const liveSessionAlerts = (items = [], nowMs = Date.now()) =>
  liveActiveSessionNotifications((items || []).map(sanitizeInAppAlert).filter(Boolean), nowMs);

/**
 * Inbox route for an alert.
 *
 * @param {Object} item
 * @param {Object} [options]
 * @param {boolean} [options.enter] when true, Join opens the live session
 * @returns {{ name: string, params: { id: string }, hash?: string, search?: Object }}
 */
export const alertRoute = (item, options = {}) => {
  const id = typeof item?.transactionId === 'string' ? item.transactionId : '';
  const kind = item?.kind;
  const route = {
    name: item?.role === 'provider' ? 'SaleDetailsPage' : 'OrderDetailsPage',
    params: { id },
  };
  if (JOIN_KINDS.has(kind)) {
    route.hash = `#${JOIN_SESSION_HASH}`;
    if (options.enter) {
      route.search = { [JOIN_SESSION_ENTER_PARAM]: '1' };
    }
  }
  return route;
};

/**
 * True for Start now Join alerts (assigned time is now).
 *
 * @param {Object|string} [item]
 * @returns {boolean}
 */
export const isStartNowJoinAlert = item => {
  if (!item || typeof item !== 'object') {
    return false;
  }
  return (
    item.kind === 'start_now' || item.preview === 'Join now' || item.preview === 'Join Session'
  );
};

/**
 * Show Join on both sides. Hide only when this person is already on
 * that booking — the page Join covers it then.
 *
 * @param {Object|null} item
 * @param {boolean} [viewingSameBooking]
 * @returns {boolean}
 */
export const shouldSuppressJoinPopup = (item, viewingSameBooking) => {
  if (!item) {
    return true;
  }
  return !!viewingSameBooking;
};

/**
 * Visiting a Start now order/sale must not clear Join now — Pay redirects
 * there immediately, and both sides still need the popup and bell.
 *
 * @param {Object|null} transaction
 * @returns {boolean}
 */
export const shouldMarkInAppTransactionReadOnVisit = transaction =>
  transaction?.attributes?.protectedData?.startNow !== true;

/**
 * NamedLink `to` for an alert (hash + optional enter search).
 *
 * @param {Object} item
 * @param {Object} [options]
 * @returns {{ hash?: string, search?: string }}
 */
export const alertLinkTo = (item, options = {}) => {
  const route = alertRoute(item, options);
  const to = {};
  if (route.hash) {
    to.hash = route.hash;
  }
  if (route.search && typeof route.search === 'object') {
    const parts = Object.keys(route.search)
      .filter(key => route.search[key] != null && route.search[key] !== '')
      .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(route.search[key])}`);
    if (parts.length > 0) {
      to.search = parts.join('&');
    }
  }
  return to;
};

/**
 * Ask the header poll to fetch alerts now (after Start now Accept).
 */
export const requestInAppNotificationRefresh = () => {
  if (typeof window === 'undefined') {
    return;
  }
  window.dispatchEvent(new Event(IN_APP_NOTIFICATION_REFRESH_EVENT));
};

/**
 * Translation id for a bell or toast row.
 *
 * @param {string|Object} kindOrItem
 * @returns {string}
 */
/**
 * True when the toast/popup should be a Join entry point.
 *
 * @param {Object} [item]
 * @returns {boolean}
 */
export const isJoinAlert = item => JOIN_KINDS.has(item?.kind);

/**
 * Booking-flow format label for bell / toast / Join popup copy.
 *
 * @param {Object|string} [item]
 * @returns {string}
 */
export const alertFormatLabel = item => {
  const format = typeof item === 'string' ? item : item?.sessionFormat;
  return sessionFormatDisplayName(format);
};

/**
 * The other party's public screen name.
 * Talker (customer) sees the listener listing title. Listener (provider)
 * sees the talker’s Talking screen name. Account First L only as fallback.
 *
 * @param {Object} [item]
 * @returns {string|undefined}
 */
export const alertPartyName = item => {
  const other = typeof item?.otherDisplayName === 'string' ? item.otherDisplayName.trim() : '';
  const listing = typeof item?.listingTitle === 'string' ? item.listingTitle.trim() : '';
  const role = item?.role;
  const isCustomer = role === 'customer' || role === 'talker' || role === 'order' || role == null;
  const isProvider = role === 'provider' || role === 'listener' || role === 'sale';
  if (isProvider) {
    return other || listing || undefined;
  }
  if (isCustomer) {
    return listing || other || undefined;
  }
  return other || listing || undefined;
};

const alertNameFallback = (item, intl) => {
  const listenerView = item?.role === 'provider' || item?.role === 'listener' || item?.role === 'sale';
  const id = listenerView ? 'NotificationMenu.nameFallbackTalker' : 'NotificationMenu.nameFallbackListener';
  if (intl && typeof intl.formatMessage === 'function') {
    try {
      return intl.formatMessage({ id });
    } catch (e) {
      // fall through
    }
  }
  return listenerView ? 'your Talker' : 'your Listener';
};

/**
 * Values for NotificationMenu join/accept rows.
 *
 * @param {Object} [item]
 * @returns {{ name: string, format: string }}
 */
const ALERT_STATUS_IDS = {
  cancelled: 'NotificationMenu.statusCancelled',
  declined: 'NotificationMenu.statusDeclined',
  expired: 'NotificationMenu.statusExpired',
  completed: 'NotificationMenu.statusCompleted',
};
const ALERT_STATUS_FALLBACK = {
  cancelled: 'cancelled',
  declined: 'declined',
  expired: 'expired',
  completed: 'completed',
};

/**
 * Plain status word for "Your booking with {name} was {status}." Every
 * caller (toast, Join popup, bell) must pass it: a missing ICU value makes
 * react-intl print the raw "{name} ... {status}" template.
 *
 * @param {Object} [item]
 * @param {Object} [intl]
 * @returns {string}
 */
export const alertStatusLabel = (item, intl) => {
  const outcome = bookingAlertOutcome(item) || 'cancelled';
  const key = ALERT_STATUS_IDS[outcome] ? outcome : 'cancelled';
  if (intl && typeof intl.formatMessage === 'function') {
    try {
      return intl.formatMessage({ id: ALERT_STATUS_IDS[key] });
    } catch (e) {
      // fall through
    }
  }
  return ALERT_STATUS_FALLBACK[key];
};

export const alertMessageValues = (item, intl) => ({
  name: alertPartyName(item) || alertNameFallback(item, intl),
  status: alertStatusLabel(item, intl),
  format: alertFormatLabel(item),
  ...(item?.kind === IN_APP_KIND_TIP
    ? { amount: item.tipAmount || '', earn: item.tipEarn || item.tipAmount || '' }
    : {}),
});

/**
 * Relative time parts for a bell row. Past events use negative values.
 *
 * @param {string} [createdAt]
 * @param {number} [nowMs]
 * @returns {{ value: number, unit: string }|null}
 */
export const alertRelativeTimeParts = (createdAt, nowMs = Date.now()) => {
  const ms = Date.parse(createdAt);
  if (!Number.isFinite(ms)) {
    return null;
  }
  const diffSec = (ms - nowMs) / 1000;
  const abs = Math.abs(diffSec);
  if (abs < 45) {
    return { value: Math.round(diffSec), unit: 'second' };
  }
  if (abs < 45 * 60) {
    return { value: Math.round(diffSec / 60), unit: 'minute' };
  }
  if (abs < 22 * 3600) {
    return { value: Math.round(diffSec / 3600), unit: 'hour' };
  }
  if (abs < 26 * 86400) {
    return { value: Math.round(diffSec / 86400), unit: 'day' };
  }
  return { value: Math.round(diffSec / (7 * 86400)), unit: 'week' };
};

/**
 * True when the bell row should use the reminder glyph (clock-in-blush).
 *
 * @param {Object|string} [item]
 * @returns {boolean}
 */
export const isReminderAlert = item => {
  const kind = typeof item === 'string' ? item : item?.kind;
  return kind === IN_APP_KIND_ACCEPT_REMINDER || kind === IN_APP_KIND_PENDING_REQUEST;
};

/**
 * Second line under a bell/toast title. Format only — the screen name
 * already lives in the title.
 *
 * @param {Object} [item]
 * @returns {string|null}
 */
export const alertPreviewText = item => {
  if (item?.kind === IN_APP_KIND_MESSAGE && item.preview) {
    return item.preview;
  }
  return item?.sessionFormat ? alertFormatLabel(item) : null;
};

const COMPLETED_ALERT_TRANSITIONS = new Set([
  'transition/complete',
  'transition/operator-complete',
  'transition/operator-complete-separate-hold',
  'transition/charge-after-session',
  'transition/provider-charge-after-session',
  'transition/review-1-by-provider',
  'transition/review-2-by-provider',
  'transition/expire-provider-review-period',
]);

const CUSTOMER_REVIEWED_TRANSITIONS = new Set([
  'transition/review-1-by-customer',
  'transition/review-2-by-customer',
  'transition/expire-review-period',
  'transition/expire-customer-review-period',
]);

const EXPIRED_ALERT_TRANSITIONS = new Set([
  'transition/expire',
  'transition/expire-accepted',
  'transition/operator-cancel-separate-hold',
  'transition/expire-payment',
]);

const CANCELLED_ALERT_TRANSITIONS = new Set([
  'transition/cancel',
  'transition/customer-cancel',
  'transition/provider-cancel',
  'transition/decline',
  'transition/operator-decline',
]);

const isCustomerAlertRole = role => role !== 'provider' && role !== 'listener' && role !== 'sale';

/**
 * True when the booked 15-minute slot is over, or the booking already
 * completed / expired / cancelled. Do not use createdAt — accept can
 * land days before the session.
 *
 * @param {Object} [item]
 * @param {number} [nowMs]
 * @returns {boolean}
 */
export const isPastBookingAlert = (item, nowMs = Date.now()) => {
  if (!item) {
    return false;
  }
  if (item.kind === IN_APP_KIND_DECLINED || item.kind === IN_APP_KIND_CANCELLED) {
    return true;
  }
  const last = item.lastTransition;
  if (
    COMPLETED_ALERT_TRANSITIONS.has(last) ||
    CUSTOMER_REVIEWED_TRANSITIONS.has(last) ||
    EXPIRED_ALERT_TRANSITIONS.has(last) ||
    CANCELLED_ALERT_TRANSITIONS.has(last)
  ) {
    return true;
  }
  const endMs = notificationBookedEndMs(item);
  return Number.isFinite(endMs) && nowMs > endMs;
};

/**
 * Current booking outcome for a stale accept / pending row.
 *
 * @param {Object} [item]
 * @param {number} [nowMs]
 * @returns {'completed'|'expired'|'cancelled'|'declined'|null}
 */
export const bookingAlertOutcome = (item, nowMs = Date.now()) => {
  const last = item?.lastTransition;
  if (
    item?.kind === IN_APP_KIND_DECLINED ||
    last === 'transition/decline' ||
    last === 'transition/operator-decline'
  ) {
    return 'declined';
  }
  if (item?.kind === IN_APP_KIND_CANCELLED || CANCELLED_ALERT_TRANSITIONS.has(last)) {
    return 'cancelled';
  }
  if (EXPIRED_ALERT_TRANSITIONS.has(last)) {
    return 'expired';
  }
  if (COMPLETED_ALERT_TRANSITIONS.has(last) || CUSTOMER_REVIEWED_TRANSITIONS.has(last)) {
    return 'completed';
  }
  if (!isPastBookingAlert(item, nowMs)) {
    return null;
  }
  if (item?.kind === IN_APP_KIND_PENDING_REQUEST || item?.kind === IN_APP_KIND_ACCEPT_REMINDER) {
    return 'expired';
  }
  return 'completed';
};

export const ALERT_ACTION_VIEW_BOOKING = 'view_booking';
export const ALERT_ACTION_LEAVE_REVIEW = 'leave_review';
export const ALERT_ACTION_ACCEPT_DECLINE = 'accept_decline';

/**
 * True when a completed talk session still needs a public customer review.
 *
 * @param {Object} [item]
 * @returns {boolean}
 */
export const canLeaveReviewFromAlert = item => {
  if (!item || !isCustomerAlertRole(item.role)) {
    return false;
  }
  const last = item.lastTransition;
  if (CUSTOMER_REVIEWED_TRANSITIONS.has(last)) {
    return false;
  }
  return (
    COMPLETED_ALERT_TRANSITIONS.has(last) ||
    last === 'transition/complete' ||
    last === 'transition/charge-after-session'
  );
};

/**
 * Action pill for a bell row. Past bookings keep Leave a review only.
 *
 * @param {Object} [item]
 * @param {number} [nowMs]
 * @returns {'view_booking'|'leave_review'|'accept_decline'|null}
 */
export const alertActionKind = (item, nowMs = Date.now()) => {
  if (!item) {
    return null;
  }
  if (isPastBookingAlert(item, nowMs)) {
    return canLeaveReviewFromAlert(item) ? ALERT_ACTION_LEAVE_REVIEW : null;
  }
  if (item.kind === IN_APP_KIND_ACCEPTED) {
    return ALERT_ACTION_VIEW_BOOKING;
  }
  if (item.kind === IN_APP_KIND_PENDING_REQUEST || item.kind === IN_APP_KIND_ACCEPT_REMINDER) {
    return ALERT_ACTION_ACCEPT_DECLINE;
  }
  return null;
};

export const alertMessageId = (kindOrItem, nowMs = Date.now()) => {
  const item = typeof kindOrItem === 'object' && kindOrItem ? kindOrItem : { kind: kindOrItem };
  const kind = item.kind;
  if (kind === IN_APP_KIND_ACCEPTED) {
    const outcome = bookingAlertOutcome(item, nowMs);
    if (outcome === 'completed') {
      return 'NotificationMenu.sessionCompleted';
    }
    if (outcome === 'expired') {
      return 'NotificationMenu.sessionExpired';
    }
    if (outcome === 'cancelled') {
      return 'NotificationMenu.cancelled';
    }
    return 'NotificationMenu.accepted';
  }
  if (kind === IN_APP_KIND_PENDING_REQUEST) {
    if (bookingAlertOutcome(item, nowMs) === 'expired') {
      return 'NotificationMenu.sessionExpired';
    }
    return 'NotificationMenu.pendingRequest';
  }
  if (kind === IN_APP_KIND_ACCEPT_REMINDER) {
    if (bookingAlertOutcome(item, nowMs) === 'expired') {
      return 'NotificationMenu.sessionExpired';
    }
    return 'NotificationMenu.acceptReminder';
  }
  if (kind === IN_APP_KIND_DECLINED) {
    if (item.outcome === 'slot_taken') {
      return 'NotificationMenu.declinedSlotTaken';
    }
    if (item.outcome === 'suggested_times') {
      return 'NotificationMenu.declinedWithSuggestions';
    }
    return 'NotificationMenu.declined';
  }
  if (kind === IN_APP_KIND_CANCELLED) {
    const listenerView = item.role === 'provider' || item.role === 'listener';
    if (item.outcome === 'removed_self') {
      return 'NotificationMenu.removedSelf';
    }
    if (item.outcome === 'no_show_refunded' || item.outcome === 'no_show_not_charged') {
      return listenerView
        ? 'NotificationMenu.cancelledNoShowListener'
        : item.outcome === 'no_show_refunded'
        ? 'NotificationMenu.cancelledNoShowRefunded'
        : 'NotificationMenu.cancelledNoShowNotCharged';
    }
    return 'NotificationMenu.cancelled';
  }
  if (kind === IN_APP_KIND_REVIEW) {
    return 'NotificationMenu.review';
  }
  if (kind === IN_APP_KIND_PAYOUT) {
    return 'NotificationMenu.payout';
  }
  if (kind === IN_APP_KIND_TIP) {
    return 'NotificationMenu.tip';
  }
  if (kind === IN_APP_KIND_REMOVED) {
    return item.outcome === 'not_charged'
      ? 'NotificationMenu.removedNotCharged'
      : item.outcome === 'still_paid'
      ? 'NotificationMenu.removedStillPaid'
      : 'NotificationMenu.removed';
  }
  if (isStartNowJoinAlert(item)) {
    return 'NotificationMenu.joinReadyNow';
  }
  if (kind === 'starting_soon') {
    return 'NotificationMenu.startingSoon';
  }
  if (kind === 'buzz') {
    return 'NotificationMenu.buzz';
  }
  if (JOIN_KINDS.has(kind)) {
    return 'NotificationMenu.joinReady';
  }
  return 'NotificationMenu.message';
};

/**
 * One row per transaction, newest event wins (message after accept).
 *
 * @param {Array<Object>} items
 * @returns {Array<Object>}
 */
const alertTransactionId = item => item?.transactionId || item?.id;

/**
 * Drop Join alerts after cancel / no-show / slot end. Keep last known
 * non-join rows on a 429.
 *
 * @param {Array<Object>} items
 * @param {Object} [options]
 * @param {Array} [options.transactions]
 * @param {number} [options.nowMs]
 * @returns {Array<Object>}
 */
export const reconcileInAppAlerts = (
  items = [],
  { transactions = [], nowMs = Date.now() } = {}
) => {
  const byId = (Array.isArray(transactions) ? transactions : []).reduce((acc, tx) => {
    const id = tx?.id?.uuid || (typeof tx?.id === 'string' ? tx.id : null);
    if (id) {
      acc[id] = tx;
    }
    return acc;
  }, {});
  return (items || []).filter(item => {
    if (!JOIN_KINDS.has(item?.kind)) {
      return true;
    }
    if (isTerminalJoinTransition(item.lastTransition)) {
      return false;
    }
    if (!isJoinableDashboardNotification(item, nowMs)) {
      return false;
    }
    const tx = byId[alertTransactionId(item)];
    if (tx && !isStillJoinableTransaction(tx, nowMs)) {
      return false;
    }
    return true;
  });
};

const BOOKING_STATUS_KINDS = new Set([
  IN_APP_KIND_ACCEPTED,
  IN_APP_KIND_DECLINED,
  IN_APP_KIND_CANCELLED,
  IN_APP_KIND_PENDING_REQUEST,
  IN_APP_KIND_ACCEPT_REMINDER,
  IN_APP_KIND_REVIEW,
  IN_APP_KIND_PAYOUT,
  IN_APP_KIND_TIP,
  IN_APP_KIND_REMOVED,
]);

// A tip stays the row for that booking; a later payout / review does not hide it.
const TIP_OUTRANKS = new Set([IN_APP_KIND_PAYOUT, IN_APP_KIND_REVIEW]);

const shouldReplaceCollapsedAlert = (prev, item) => {
  const nextMs = Date.parse(item.createdAt) || 0;
  const prevMs = Date.parse(prev.createdAt) || 0;
  // A moderation removal settles by cancelling the booking a moment later;
  // "<name> was removed" stays the row, never "was cancelled".
  if (prev.kind === IN_APP_KIND_REMOVED && item.kind === IN_APP_KIND_CANCELLED) {
    return false;
  }
  if (item.kind === IN_APP_KIND_REMOVED && prev.kind === IN_APP_KIND_CANCELLED) {
    return true;
  }
  if (item.kind === IN_APP_KIND_MESSAGE && BOOKING_STATUS_KINDS.has(prev.kind)) {
    return false;
  }
  if (prev.kind === IN_APP_KIND_TIP && TIP_OUTRANKS.has(item.kind)) {
    return false;
  }
  if (item.kind === IN_APP_KIND_TIP && TIP_OUTRANKS.has(prev.kind)) {
    return true;
  }
  if (BOOKING_STATUS_KINDS.has(item.kind) && prev.kind === IN_APP_KIND_MESSAGE) {
    return true;
  }
  if (nextMs !== prevMs) {
    return nextMs > prevMs;
  }
  return (KIND_PRIORITY[item.kind] || 0) >= (KIND_PRIORITY[prev.kind] || 0);
};

export const collapseAlertsByTransaction = items => {
  const byTx = new Map();
  (items || []).forEach(item => {
    if (!item?.transactionId) {
      return;
    }
    const prev = byTx.get(item.transactionId);
    if (!prev || shouldReplaceCollapsedAlert(prev, item)) {
      byTx.set(item.transactionId, item);
    }
  });
  return [...byTx.values()].sort(
    (a, b) => (Date.parse(b.createdAt) || 0) - (Date.parse(a.createdAt) || 0)
  );
};

const canUseStorage = () => typeof window !== 'undefined' && !!window.localStorage;

const readStore = () => {
  if (!canUseStorage()) {
    return { users: {} };
  }
  try {
    const parsed = JSON.parse(window.localStorage.getItem(IN_APP_NOTIFICATION_STORAGE_KEY));
    if (parsed && typeof parsed === 'object' && parsed.users && typeof parsed.users === 'object') {
      return parsed;
    }
  } catch (e) {
    // Corrupt storage — start clean so the bell still works.
  }
  return { users: {} };
};

const writeStore = (store, { silent = false } = {}) => {
  if (!canUseStorage()) {
    return;
  }
  window.localStorage.setItem(IN_APP_NOTIFICATION_STORAGE_KEY, JSON.stringify(store));
  if (!silent) {
    window.dispatchEvent(new Event(IN_APP_NOTIFICATION_CHANGE_EVENT));
  }
};

const userBucket = (store, userId) => {
  const existing = store.users[userId];
  if (existing && typeof existing === 'object') {
    return {
      readAtByTx:
        existing.readAtByTx && typeof existing.readAtByTx === 'object' ? existing.readAtByTx : {},
      toastedIds: Array.isArray(existing.toastedIds) ? existing.toastedIds : [],
      deletedAtByTx:
        existing.deletedAtByTx && typeof existing.deletedAtByTx === 'object'
          ? existing.deletedAtByTx
          : {},
    };
  }
  return emptyUserState();
};

/**
 * Subscribe to local read-state changes (same tab + other tabs).
 *
 * @param {Function} listener
 * @returns {Function} unsubscribe
 */
export const subscribeInAppNotificationStore = listener => {
  if (typeof window === 'undefined') {
    return () => {};
  }
  const onChange = () => listener();
  window.addEventListener(IN_APP_NOTIFICATION_CHANGE_EVENT, onChange);
  window.addEventListener('storage', onChange);
  return () => {
    window.removeEventListener(IN_APP_NOTIFICATION_CHANGE_EVENT, onChange);
    window.removeEventListener('storage', onChange);
  };
};

/**
 * True when the event happened after the last time this order was opened.
 *
 * @param {Object} item
 * @param {Object} userState
 * @returns {boolean}
 */
export const isInAppAlertUnread = (item, userState) => {
  const readAt = userState?.readAtByTx?.[item?.transactionId];
  if (!readAt) {
    return true;
  }
  const eventMs = Date.parse(item?.createdAt);
  const readMs = Date.parse(readAt);
  if (!Number.isFinite(eventMs) || !Number.isFinite(readMs)) {
    return true;
  }
  return eventMs > readMs;
};

/**
 * True when this booking's alerts were deleted at/after this event.
 * Server filters too (durable, cross-device); this is the instant local copy.
 *
 * @param {Object} item
 * @param {Object} userState
 * @returns {boolean}
 */
export const isInAppAlertDeleted = (item, userState) => {
  const deletedAt = userState?.deletedAtByTx?.[item?.transactionId];
  if (!deletedAt) {
    return false;
  }
  const eventMs = Date.parse(item?.createdAt);
  return !(Number.isFinite(eventMs) && eventMs > Date.parse(deletedAt));
};

/**
 * Locally hide (or un-hide on failure) one booking's alerts.
 *
 * @param {string} userId
 * @param {string} transactionId
 * @param {string|null} deletedAt null removes the local hide
 */
export const setInAppAlertDeleted = (userId, transactionId, deletedAt) => {
  if (!userId || !transactionId || !canUseStorage()) {
    return;
  }
  const store = readStore();
  const current = userBucket(store, userId);
  const deletedAtByTx = { ...current.deletedAtByTx };
  if (deletedAt) {
    deletedAtByTx[transactionId] = deletedAt;
  } else {
    delete deletedAtByTx[transactionId];
  }
  store.users[userId] = { ...current, deletedAtByTx };
  writeStore(store);
};

/**
 * Attach unread flags and cap the list.
 *
 * @param {string} userId
 * @param {Array<Object>} items
 * @returns {{ items: Array<Object>, unreadCount: number, userState: Object }}
 */
export const decorateInAppAlerts = (userId, items) => {
  const store = readStore();
  const userState = userId ? userBucket(store, userId) : emptyUserState();
  const collapsed = collapseAlertsByTransaction(
    reconcileInAppAlerts((items || []).map(sanitizeInAppAlert).filter(Boolean))
  ).filter(item => !isInAppAlertDeleted(item, userState));
  const decorated = takeInAppAlertsPerRole(collapsed).map(item => ({
    ...item,
    unread: isInAppAlertUnread(item, userState),
  }));
  return {
    items: decorated,
    unreadCount: decorated.filter(item => item.unread).length,
    userState,
  };
};

/**
 * Mark one order/sale thread as read.
 *
 * @param {string} userId
 * @param {string} transactionId
 * @param {string} [readAt]
 */
export const markInAppTransactionRead = (
  userId,
  transactionId,
  readAt = new Date().toISOString()
) => {
  if (!userId || !transactionId || !canUseStorage()) {
    return;
  }
  const store = readStore();
  const current = userBucket(store, userId);
  const previous = current.readAtByTx[transactionId];
  if (previous && Date.parse(previous) >= Date.parse(readAt)) {
    return;
  }
  store.users[userId] = {
    ...current,
    readAtByTx: {
      ...current.readAtByTx,
      [transactionId]: readAt,
    },
  };
  writeStore(store);
};

/**
 * Mark every listed alert's booking as read (opening the bell).
 *
 * @param {string} userId
 * @param {Array<Object>} items
 * @param {string} [readAt]
 */
export const markAllInAppAlertsRead = (userId, items, readAt = new Date().toISOString()) => {
  if (!userId || !canUseStorage()) {
    return;
  }
  const store = readStore();
  const current = userBucket(store, userId);
  const readAtByTx = { ...current.readAtByTx };
  let changed = false;
  (items || []).forEach(item => {
    const transactionId = item?.transactionId;
    if (!transactionId) {
      return;
    }
    const previous = readAtByTx[transactionId];
    if (previous && Date.parse(previous) >= Date.parse(readAt)) {
      return;
    }
    readAtByTx[transactionId] = readAt;
    changed = true;
  });
  if (!changed) {
    return;
  }
  store.users[userId] = {
    ...current,
    readAtByTx,
  };
  writeStore(store);
};

/**
 * Remember which alerts already toasted so a refresh does not pop them again.
 *
 * @param {string} userId
 * @param {Array<string>} ids
 */
export const rememberToastedInAppAlerts = (userId, ids) => {
  if (!userId || !canUseStorage()) {
    return;
  }
  const incoming = (ids || []).filter(id => typeof id === 'string' && id.length > 0);
  if (incoming.length === 0) {
    return;
  }
  const store = readStore();
  const current = userBucket(store, userId);
  const merged = [...new Set([...current.toastedIds, ...incoming])].slice(-80);
  store.users[userId] = {
    ...current,
    toastedIds: merged,
  };
  writeStore(store, { silent: true });
};

/**
 * Alerts that should toast while the person is already on the site.
 * First successful fetch is silent (backfill).
 *
 * @param {Array<Object>} items
 * @param {Object} userState
 * @param {boolean} hasHydrated
 * @returns {Array<Object>}
 */
export const alertsToToast = (items, userState, hasHydrated) => {
  if (!hasHydrated) {
    return [];
  }
  const toasted = new Set(userState?.toastedIds || []);
  return (items || []).filter(
    item =>
      // A tip pops even if the booking page was open (that page marks the
      // whole booking read on every refetch, which hid the tip toast on
      // v636). toastedIds still keeps it to one pop-up per tip.
      (item?.unread || isFreshTip(item)) &&
      item?.id &&
      !toasted.has(item.id) &&
      !isJoinAlert(item) &&
      item.kind !== IN_APP_KIND_PENDING_REQUEST &&
      item.kind !== IN_APP_KIND_ACCEPT_REMINDER &&
      item.kind !== IN_APP_KIND_ACCEPTED &&
      item.kind !== IN_APP_KIND_DECLINED
  );
};

/**
 * Tips that should still toast on the first (backfill) fetch: a talker often
 * tips seconds after the call, after the listener's tab has moved on, so the
 * next page load is the first time the browser sees it (Oct 6 2026).
 *
 * @param {Array<Object>} items
 * @param {Object} userState
 * @param {number} [nowMs]
 * @returns {Array<Object>}
 */
export const TIP_TOAST_ON_LOAD_MS = 24 * 60 * 60 * 1000;
function isFreshTip(item, nowMs = Date.now()) {
  if (item?.kind !== IN_APP_KIND_TIP) {
    return false;
  }
  const at = Date.parse(item.createdAt);
  return Number.isFinite(at) && nowMs - at <= TIP_TOAST_ON_LOAD_MS;
}
export const tipAlertsToToastOnLoad = (items, userState, nowMs = Date.now()) => {
  const toasted = new Set(userState?.toastedIds || []);
  return (items || []).filter(item => {
    if (item?.kind !== IN_APP_KIND_TIP || !item.id || toasted.has(item.id)) {
      return false;
    }
    return isFreshTip(item, nowMs);
  });
};

/**
 * Join-ready / Start now alerts that should open the on-screen Join popup.
 * Shown on first load too — unlike toasts — so an open tab is not silent.
 *
 * @param {Array<Object>} items
 * @param {Object} userState
 * @returns {Array<Object>}
 */
export const alertsToJoinPopup = (items, userState) => {
  const toasted = new Set(userState?.toastedIds || []);
  return (items || []).filter(
    item =>
      isJoinAlert(item) &&
      item?.sessionEnded !== true &&
      item?.kind !== 'starting_soon' &&
      !(item?.kind === IN_APP_KIND_JOIN_READY && !isStartNowJoinAlert(item)) &&
      item?.unread &&
      item?.id &&
      !toasted.has(item.id)
  );
};
